/**
 * Clipboard copy/paste handlers with token support
 */

import {EditorView} from "@codemirror/view";
import {Token} from "../models/editor.types";
import {tokensField, setTokensEffect} from "../state/editor.state";
import {
  serializeRecipe,
  parseSerializedRecipeFromClipboard,
  extractSerializedFromClipboardHtml
} from "../serialization/recipe-serializer";

/**
 * Escape HTML for clipboard
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Serialize selection for clipboard (with HTML wrapper)
 */
export function serializeSelectionForClipboard(
  view: EditorView
): { plain: string; html: string } | null {
  const state = view.state;
  const sel = state.selection.main;

  if (sel.empty) return null;

  const from = sel.from;
  const to = sel.to;

  const fullText = state.doc.toString();
  const plain = fullText.slice(from, to);

  // Clip tokens to selection
  const tokens = state.field(tokensField);
  const clipped = tokens
    .filter((t) => t.type !== "ingredient_draft")
    .map((t) => {
      const a = Math.max(t.from, from);
      const b = Math.min(t.to, to);
      if (b <= a) return null;
      return {from: a - from, to: b - from, type: t.type};
    })
    .filter(Boolean) as Token[];

  const serialized = serializeRecipe(plain, clipped);
  const html = `<div data-recipe-editor="v1">${serialized}</div>`;

  return {plain, html};
}

/**
 * Insert parsed content at selection
 */
export function insertParsedAtSelection(
  view: EditorView,
  parsed: { text: string; tokens: Token[] }
): void {
  const state = view.state;
  const sel = state.selection.main;

  const insertText = parsed.text ?? "";
  const insertLen = insertText.length;

  const from = sel.from;
  const to = sel.to;

  const existing = state.field(tokensField).filter((t) => t.type !== "ingredient_draft");

  // Remove/clip overlapping tokens
  const kept: Token[] = [];
  for (const t of existing) {
    if (t.to <= from) {
      kept.push(t);
      continue;
    }
    if (t.from >= to) {
      kept.push(t);
      continue;
    }
    // Overlapping: drop it
  }

  const delta = insertLen - (to - from);

  // Shift tokens after insertion point
  const shifted: Token[] = kept.map((t) => {
    if (t.from >= to) {
      return {...t, from: t.from + delta, to: t.to + delta};
    }
    return t;
  });

  // Add inserted tokens
  const insertedTokens: Token[] = parsed.tokens
    .filter((t) => t.type !== "ingredient_draft")
    .map((t) => ({
      from: from + t.from,
      to: from + t.to,
      type: t.type,
    }));

  const nextTokens = shifted.concat(insertedTokens);

  view.dispatch({
    changes: {from, to, insert: insertText},
    effects: setTokensEffect.of(nextTokens),
    selection: {anchor: from + insertLen, head: from + insertLen},
    scrollIntoView: true,
  });
}

/**
 * Create clipboard copy handler
 */
export function createClipboardCopyHandler() {
  return EditorView.domEventHandlers({
    copy: (event, view) => {
      const payload = serializeSelectionForClipboard(view);
      if (!payload) return false;

      event.preventDefault();
      event.clipboardData?.setData("text/plain", payload.plain);
      event.clipboardData?.setData("text/html", payload.html);

      return true;
    },
    cut: (event, view) => {
      const payload = serializeSelectionForClipboard(view);
      if (!payload) return false;

      event.preventDefault();
      event.clipboardData?.setData("text/plain", payload.plain);
      event.clipboardData?.setData("text/html", payload.html);

      // Delete selection
      view.dispatch({
        changes: {
          from: view.state.selection.main.from,
          to: view.state.selection.main.to,
          insert: "",
        },
      });

      return true;
    },
  });
}

/**
 * Create clipboard paste handler
 */
export function createClipboardPasteHandler() {
  return EditorView.domEventHandlers({
    paste: (event, view) => {
      const clipData = event.clipboardData;
      if (!clipData) return false;

      const html = clipData.getData("text/html");
      const serialized = extractSerializedFromClipboardHtml(html);

      if (serialized) {
        event.preventDefault();
        const parsed = parseSerializedRecipeFromClipboard(serialized);
        insertParsedAtSelection(view, parsed);
        return true;
      }

      // Fallback to plain text (default behavior)
      return false;
    },
  });
}
